import type { DSHookFloatingContextT } from '../react-desc-prop-types.js';

type RectLike = { top: number; left: number; right: number; bottom: number };
type OverflowOffsets = { top: number; bottom: number; left: number; right: number };

const findFixedParent = (el: Element | null) => {
  let element = el;
  while (element && element !== document.body) {
    const style = getComputedStyle(element);
    // IMPORTANT: only treat FIXED as special. Absolute parents are common and scroll,
    // and using their rect as an offset breaks positioning on scroll.
    if (style.position === 'fixed') {
      return element;
    }
    element = element.parentElement;
  }
  return null;
};

const adjustForFixedParent = (child: Element) => {
  const fixedParent = findFixedParent(child);
  if (fixedParent) {
    const rect = fixedParent.getBoundingClientRect();
    return { top: rect.top, left: rect.left };
  }
  return { top: 0, left: 0 };
};

/**
 * Finds nearest ancestor that can CLIP the floating element.
 * This is typically the correct boundary when rendering without portal.
 */
const getClippingParent = (el: Element | null): Element | null => {
  let node = el?.parentElement ?? null;

  while (node && node !== document.body) {
    const style = getComputedStyle(node);

    const overflowX = style.overflowX || style.overflow;
    const overflowY = style.overflowY || style.overflow;

    const clipsX = /(auto|scroll|hidden|clip)/.test(overflowX);
    const clipsY = /(auto|scroll|hidden|clip)/.test(overflowY);

    if (clipsX || clipsY) return node;

    node = node.parentElement;
  }

  return null;
};

const getViewportRect = (): RectLike => ({
  top: 0,
  left: 0,
  right: window.innerWidth,
  bottom: window.innerHeight,
});

/**
 * Shift behavior: if any overflow side is positive, push the popper back inside.
 * Similar to Floating UI "shift" middleware.
 */
const applyShift = (x: number, y: number, overflow: OverflowOffsets) => {
  let nextX = x;
  let nextY = y;

  if (overflow.left > 0) nextX += overflow.left;
  if (overflow.right > 0) nextX -= overflow.right;

  if (overflow.top > 0) nextY += overflow.top;
  if (overflow.bottom > 0) nextY -= overflow.bottom;

  return { x: nextX, y: nextY };
};

const fits = (o: OverflowOffsets) => o.top <= 0 && o.bottom <= 0 && o.left <= 0 && o.right <= 0;

const getOverflowScore = (o: OverflowOffsets) => {
  const top = Math.max(0, o.top);
  const bottom = Math.max(0, o.bottom);
  const left = Math.max(0, o.left);
  const right = Math.max(0, o.right);

  const total = top + bottom + left + right;
  const maxSide = Math.max(top, bottom, left, right);

  return { total, maxSide };
};

/**
 * Ensure we actually TRY variations (start/end) for each base placement.
 * Fixes cases where only e.g. "bottom-end" fits but would never be tried.
 */
const expandWithVariations = (pls: DSHookFloatingContextT.PopperPlacementsT[]) => {
  const out: DSHookFloatingContextT.PopperPlacementsT[] = [];
  const seen = new Set<string>();

  const add = (p: string) => {
    if (!seen.has(p)) {
      seen.add(p);
      out.push(p as DSHookFloatingContextT.PopperPlacementsT);
    }
  };

  pls.forEach((p) => {
    add(p);

    const [base, variation] = p.split('-');
    if (!variation) {
      add(`${base}-start`);
      add(`${base}-end`);
    }
  });

  return out;
};

/**
 * When rendering WITHOUT portal, the floating element is positioned relative to its offsetParent.
 * But referenceRect/floatingRect are in viewport coordinates, so we must convert x/y from viewport
 * space into the offsetParent's coordinate space (including its scroll offsets).
 */
const getOffsetParentData = (floating: HTMLElement) => {
  const op = floating.offsetParent as HTMLElement | null;

  // If there's no offsetParent (rare), assume viewport.
  if (!op) {
    return { left: 0, top: 0, scrollLeft: 0, scrollTop: 0 };
  }

  const opRect = op.getBoundingClientRect();

  return {
    left: opRect.left,
    top: opRect.top,
    scrollLeft: op.scrollLeft ?? 0,
    scrollTop: op.scrollTop ?? 0,
  };
};

export {
  applyShift,
  adjustForFixedParent,
  expandWithVariations,
  fits,
  getClippingParent,
  getOverflowScore,
  getOffsetParentData,
  getViewportRect,
  type RectLike,
  type OverflowOffsets,
};
